import React, { useRef, useState, useCallback, useEffect } from 'react';

interface BlogRichEditorProps {
  html: string;
  onChange: (html: string) => void;
  darkMode: boolean;
  placeholder?: string;
}

export function htmlToMarkdown(html: string): string {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function cleanHeadingText(text: string): string {
    return text.replace(/^#+\s*/g, '').trim();
  }

  function traverse(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map(traverse).join('');

    switch (tag) {
      case 'h1': {
        const clean = cleanHeadingText(children);
        return clean ? `\n\n# ${clean}\n\n` : '';
      }
      case 'h2': {
        const clean = cleanHeadingText(children);
        return clean ? `\n\n## ${clean}\n\n` : '';
      }
      case 'h3': {
        const clean = cleanHeadingText(children);
        return clean ? `\n\n### ${clean}\n\n` : '';
      }
      case 'h4':
      case 'h5':
      case 'h6': {
        const clean = cleanHeadingText(children);
        return clean ? `\n\n#### ${clean}\n\n` : '';
      }
      case 'strong':
      case 'b':
        return children.trim() ? `**${children.trim()}**` : '';
      case 'em':
      case 'i':
        return children.trim() ? `*${children.trim()}*` : '';
      case 'u':
        return children.trim() ? `<u>${children.trim()}</u>` : '';
      case 'strike':
      case 's':
      case 'del':
        return children.trim() ? `~~${children.trim()}~~` : '';
      case 'a': {
        const href = el.getAttribute('href') || '#';
        const linkText = children.trim() || href;
        return `[${linkText}](${href})`;
      }
      case 'p':
        return children.trim() ? `\n\n${children.trim()}\n\n` : '';
      case 'br':
        return '\n';
      case 'ul':
        return `\n\n${children.trim()}\n\n`;
      case 'ol':
        return `\n\n${children.trim()}\n\n`;
      case 'li': {
        const text = children.trim().replace(/^[\*\-\•]\s*/, '').replace(/^\d+\.\s*/, '');
        if (!text) return '';
        const parentTag = el.parentElement?.tagName.toLowerCase();
        if (parentTag === 'ol') {
          const idx = Array.from(el.parentElement?.children || []).indexOf(el) + 1;
          return `${idx}. ${text}\n`;
        }
        return `* ${text}\n`;
      }
      case 'blockquote': {
        const clean = children.trim().replace(/^>\s*/gm, '');
        if (!clean) return '';
        const lines = clean.split('\n');
        const quoted = lines.map(line => line.trim() ? `> ${line}` : '>').join('\n');
        return `\n\n${quoted}\n\n`;
      }
      case 'span':
        // Spans are inline styles - preserve text without injecting heading symbols
        return children;
      case 'div':
        return children.trim() ? `\n\n${children.trim()}\n\n` : '';
      default:
        return children;
    }
  }

  const rawMd = traverse(doc.body);
  return rawMd
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/(?:^|\n)(#+\s*){2,}/g, '$1')
    .trim();
}

const FONT_SIZES = [
  { label: 'Small (14px)', value: '14px' },
  { label: 'Normal (16px)', value: '16px' },
  { label: 'Medium (18px)', value: '18px' },
  { label: 'Large (20px)', value: '20px' },
  { label: 'Heading (24px)', value: '24px' },
  { label: 'Title (28px)', value: '28px' },
  { label: 'Hero (32px)', value: '32px' },
];

export default function BlogRichEditor({
  html,
  onChange,
  darkMode,
  placeholder = 'Write your blog content here...',
}: BlogRichEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeStates, setActiveStates] = useState<Record<string, boolean>>({});
  const [currentFontSize, setCurrentFontSize] = useState<string>('16px');
  const [showPlaceholder, setShowPlaceholder] = useState(!html || html === '<br>');
  const [stats, setStats] = useState({ words: 0, chars: 0, readTime: 1 });

  // Custom Undo/Redo
  const historyStack = useRef<string[]>([]);
  const historyIndex = useRef<number>(-1);
  const isNavigatingHistory = useRef<boolean>(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);

  // Link Modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const savedRangeForLink = useRef<Range | null>(null);

  const calcStats = (text: string) => {
    const clean = text.trim();
    const words = clean ? clean.split(/\s+/).filter(Boolean).length : 0;
    setStats({ words, chars: clean.length, readTime: Math.max(1, Math.ceil(words / 200)) });
  };

  const pushHistory = (newHtml: string) => {
    if (isNavigatingHistory.current) return;
    const stack = historyStack.current.slice(0, historyIndex.current + 1);
    if (stack[stack.length - 1] === newHtml) return;
    stack.push(newHtml);
    if (stack.length > 60) stack.shift();
    historyStack.current = stack;
    historyIndex.current = stack.length - 1;
  };

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
    if (isInitialMount.current) {
      el.scrollTop = 0;
      isInitialMount.current = false;
      historyStack.current = [html || ''];
      historyIndex.current = 0;
    }
    const text = el.textContent || '';
    setShowPlaceholder(!text.trim());
    calcStats(text);
  }, [html]);

  // ─────────────────────────────────────────────
  //  Active state detection
  // ─────────────────────────────────────────────
  const updateActiveStates = useCallback(() => {
    const next: Record<string, boolean> = {};
    try {
      next['bold'] = document.queryCommandState('bold');
      next['italic'] = document.queryCommandState('italic');
      next['underline'] = document.queryCommandState('underline');
      next['strikeThrough'] = document.queryCommandState('strikeThrough');
      next['insertUnorderedList'] = document.queryCommandState('insertUnorderedList');
      next['insertOrderedList'] = document.queryCommandState('insertOrderedList');

      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editorRef.current) {
        let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;

        let isQuote = false;
        let detectedSize = '';

        while (node && node !== editorRef.current) {
          const tag = (node as HTMLElement).tagName?.toLowerCase();
          if (tag === 'blockquote') isQuote = true;
          if (!detectedSize) {
            const fs = (node as HTMLElement).style?.fontSize;
            if (fs) detectedSize = fs;
          }
          node = node.parentNode;
        }

        next['blockquote'] = isQuote;

        if (detectedSize) {
          const px = Math.round(parseFloat(detectedSize)) + 'px';
          if (FONT_SIZES.find((s) => s.value === px)) setCurrentFontSize(px);
        }
      }
    } catch {
      /* ignore */
    }
    setActiveStates(next);
  }, []);

  // ─────────────────────────────────────────────
  //  Change notification
  // ─────────────────────────────────────────────
  const notifyChange = (immediate = false) => {
    const el = editorRef.current;
    if (!el) return;
    const content = el.innerHTML;
    const text = el.textContent || '';
    setShowPlaceholder(!text.trim());
    calcStats(text);
    onChange(content);
    updateActiveStates();

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (immediate) {
      pushHistory(content);
    } else {
      debounceTimer.current = setTimeout(() => pushHistory(content), 350);
    }
  };

  // ─────────────────────────────────────────────
  //  Undo / Redo
  // ─────────────────────────────────────────────
  const handleUndo = () => {
    if (historyIndex.current <= 0) return;
    historyIndex.current--;
    const prev = historyStack.current[historyIndex.current];
    isNavigatingHistory.current = true;
    if (editorRef.current) {
      editorRef.current.innerHTML = prev;
      calcStats(editorRef.current.textContent || '');
      setShowPlaceholder(!(editorRef.current.textContent || '').trim());
    }
    onChange(prev);
    updateActiveStates();
    isNavigatingHistory.current = false;
  };

  const handleRedo = () => {
    if (historyIndex.current >= historyStack.current.length - 1) return;
    historyIndex.current++;
    const next = historyStack.current[historyIndex.current];
    isNavigatingHistory.current = true;
    if (editorRef.current) {
      editorRef.current.innerHTML = next;
      calcStats(editorRef.current.textContent || '');
      setShowPlaceholder(!(editorRef.current.textContent || '').trim());
    }
    onChange(next);
    updateActiveStates();
    isNavigatingHistory.current = false;
  };

  // ─────────────────────────────────────────────
  //  Ensure focus & selection inside editor
  // ─────────────────────────────────────────────
  const ensureEditorFocus = () => {
    const el = editorRef.current;
    if (!el) return;

    // Check if editor already has focus and a valid selection inside it
    if (document.activeElement === el) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (el.contains(range.startContainer)) {
          return; // Already focused with valid selection
        }
      }
    }

    // Focus the editor
    el.focus();

    // If still no selection inside editor, place cursor at end
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !el.contains(sel.getRangeAt(0).startContainer)) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  // ─────────────────────────────────────────────
  //  Inline commands (bold, italic, etc.)
  //  Uses browser-native execCommand only.
  // ─────────────────────────────────────────────
  const execCommand = (cmd: string, value?: string) => {
    ensureEditorFocus();
    try {
      document.execCommand(cmd, false, value);
    } catch (err) {
      console.warn(`execCommand(${cmd}) failed:`, err);
    }
    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Bullet List (browser-native toggle)
  // ─────────────────────────────────────────────
  const toggleBulletList = () => {
    ensureEditorFocus();
    try {
      document.execCommand('insertUnorderedList', false, undefined);
    } catch (err) {
      console.warn('insertUnorderedList failed:', err);
    }
    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Numbered List (browser-native toggle)
  // ─────────────────────────────────────────────
  const toggleNumberedList = () => {
    ensureEditorFocus();
    try {
      document.execCommand('insertOrderedList', false, undefined);
    } catch (err) {
      console.warn('insertOrderedList failed:', err);
    }
    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Blockquote toggle (formatBlock)
  // ─────────────────────────────────────────────
  const toggleBlockquote = () => {
    ensureEditorFocus();

    // Detect if cursor is already inside a blockquote
    let isInQuote = false;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      while (node && node !== editorRef.current) {
        if ((node as HTMLElement).tagName?.toLowerCase() === 'blockquote') {
          isInQuote = true;
          break;
        }
        node = node.parentNode;
      }
    }

    try {
      document.execCommand('formatBlock', false, isInQuote ? '<p>' : '<blockquote>');
    } catch (err) {
      console.warn('formatBlock(blockquote) failed:', err);
    }
    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Font Size
  // ─────────────────────────────────────────────
  const applyFontSize = (sizePx: string) => {
    setCurrentFontSize(sizePx);
    ensureEditorFocus();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    const span = document.createElement('span');
    span.style.fontSize = sizePx;
    span.style.lineHeight = '1.4';
    if (parseInt(sizePx) >= 24) span.style.fontWeight = '700';

    try {
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      sel.addRange(newRange);
    } catch {
      /* noop */
    }

    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Link Modal
  // ─────────────────────────────────────────────
  const openLinkModal = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeForLink.current = sel.getRangeAt(0).cloneRange();
      setLinkText(sel.toString());
      setLinkUrl('');
      setShowLinkModal(true);
    }
  };

  const handleApplyLink = () => {
    if (!linkUrl.trim()) {
      setShowLinkModal(false);
      return;
    }
    const el = editorRef.current;
    if (!el) return;

    let cleanUrl = linkUrl.trim().replace(/^(?:https?:\/\/)+/i, 'https://');
    if (!/^https?:\/\//i.test(cleanUrl)) cleanUrl = `https://${cleanUrl}`;

    el.focus();
    if (savedRangeForLink.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRangeForLink.current);
      }
    }

    const a = document.createElement('a');
    a.href = cleanUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.cssText = 'color: #2563eb; text-decoration: underline; font-weight: 500; cursor: pointer;';
    a.textContent = linkText.trim() || cleanUrl;

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(a);
    }

    setShowLinkModal(false);
    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Remove citations
  // ─────────────────────────────────────────────
  const handleRemoveCitations = () => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = el.innerHTML
      .replace(/\[\s*\d+(?:[\s,\–\-]+\d+)*\s*\]/g, '')
      .replace(/\s+([.,;:?!])/g, '$1');
    notifyChange(true);
  };

  // ─────────────────────────────────────────────
  //  Keyboard shortcuts
  // ─────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      handleUndo();
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))
    ) {
      e.preventDefault();
      handleRedo();
    }
  };

  // ─────────────────────────────────────────────
  //  Toolbar mousedown handler
  //  e.preventDefault() keeps focus in the editor
  //  so the selection stays alive for execCommand.
  // ─────────────────────────────────────────────
  const tb = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    action();
  };

  const isBtnActive = (key: string) => !!activeStates[key];

  const iconBtnClass = (active: boolean) =>
    `w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer select-none ${
      active
        ? 'bg-blue-600 text-white shadow-xs'
        : darkMode
        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
        : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
    }`;

  return (
    <div
      className={`flex-1 min-h-0 flex flex-col h-full rounded-xl overflow-hidden border relative ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* ── Toolbar ── */}
      <div
        className={`shrink-0 flex items-center gap-1.5 px-4 py-2 border-b flex-wrap select-none ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}
      >
        {/* Undo / Redo */}
        <button
          type="button"
          onMouseDown={(e) => tb(e, handleUndo)}
          title="Undo (Ctrl+Z)"
          className={`${iconBtnClass(false)} ${historyIndex.current <= 0 ? 'opacity-40' : ''}`}
        >
          <i className="ri-arrow-go-back-line text-sm" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => tb(e, handleRedo)}
          title="Redo (Ctrl+Y)"
          className={`${iconBtnClass(false)} ${
            historyIndex.current >= historyStack.current.length - 1 ? 'opacity-40' : ''
          }`}
        >
          <i className="ri-arrow-go-forward-line text-sm" />
        </button>

        <div className={`w-px h-5 mx-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />

        {/* Font Size */}
        <div className="flex items-center gap-1">
          <label className={`text-[11px] font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Size:
          </label>
          <select
            value={currentFontSize}
            onChange={(e) => applyFontSize(e.target.value)}
            onMouseDown={() => {
              /* don't preventDefault — dropdown needs focus */
            }}
            className={`px-2 py-1 text-xs rounded-lg font-medium border focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
            }`}
            title="Change Font Size"
          >
            {FONT_SIZES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className={`w-px h-5 mx-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />

        {/* Bold / Italic / Underline / Strike */}
        <button type="button" onMouseDown={(e) => tb(e, () => execCommand('bold'))} title="Bold (Ctrl+B)" className={iconBtnClass(isBtnActive('bold'))}>
          <i className="ri-bold text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, () => execCommand('italic'))} title="Italic (Ctrl+I)" className={iconBtnClass(isBtnActive('italic'))}>
          <i className="ri-italic text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, () => execCommand('underline'))} title="Underline (Ctrl+U)" className={iconBtnClass(isBtnActive('underline'))}>
          <i className="ri-underline text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, () => execCommand('strikeThrough'))} title="Strikethrough" className={iconBtnClass(isBtnActive('strikeThrough'))}>
          <i className="ri-strikethrough text-sm" />
        </button>

        <div className={`w-px h-5 mx-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />

        {/* Bullet List / Numbered List / Blockquote */}
        <button type="button" onMouseDown={(e) => tb(e, toggleBulletList)} title="Bullet List" className={iconBtnClass(isBtnActive('insertUnorderedList'))}>
          <i className="ri-list-unordered text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, toggleNumberedList)} title="Numbered List" className={iconBtnClass(isBtnActive('insertOrderedList'))}>
          <i className="ri-list-ordered text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, toggleBlockquote)} title="Blockquote" className={iconBtnClass(isBtnActive('blockquote'))}>
          <i className="ri-double-quotes-l text-sm" />
        </button>

        <div className={`w-px h-5 mx-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />

        {/* Link / Unlink / Clear Format */}
        <button type="button" onMouseDown={(e) => tb(e, openLinkModal)} title="Insert Link" className={iconBtnClass(false)}>
          <i className="ri-link text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, () => execCommand('unlink'))} title="Remove Link" className={iconBtnClass(false)}>
          <i className="ri-link-unlink text-sm" />
        </button>
        <button type="button" onMouseDown={(e) => tb(e, () => execCommand('removeFormat'))} title="Clear Formatting" className={iconBtnClass(false)}>
          <i className="ri-format-clear text-sm" />
        </button>

        <div className={`w-px h-5 mx-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />

        {/* Citation Cleaner */}
        <button
          type="button"
          onMouseDown={(e) => tb(e, handleRemoveCitations)}
          title="Remove all [1], [2] citation markers"
          className={`px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 ${
            darkMode
              ? 'bg-amber-950/40 hover:bg-amber-900/60 text-amber-300 border border-amber-800/50'
              : 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200'
          }`}
        >
          <i className="ri-eraser-line text-xs" />
          <span>Remove [1] Citations</span>
        </button>
      </div>

      {/* ── Editor Canvas ── */}
      <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
        {showPlaceholder && (
          <div className="absolute top-6 left-6 text-sm text-gray-400 pointer-events-none select-none z-10">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onInput={() => notifyChange(false)}
          onKeyUp={updateActiveStates}
          onMouseUp={updateActiveStates}
          onFocus={updateActiveStates}
          className={`flex-1 min-h-0 w-full p-6 overflow-y-auto focus:outline-none leading-relaxed text-base blog-rich-editor-canvas ${
            darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'
          }`}
        />
        <style>{`
          .blog-rich-editor-canvas h1 {
            font-size: 2rem !important;
            font-weight: 700 !important;
            line-height: 1.25 !important;
            margin: 1.2em 0 0.5em !important;
          }
          .blog-rich-editor-canvas h2 {
            font-size: 1.5rem !important;
            font-weight: 700 !important;
            line-height: 1.3 !important;
            margin: 1em 0 0.4em !important;
          }
          .blog-rich-editor-canvas h3 {
            font-size: 1.25rem !important;
            font-weight: 600 !important;
            line-height: 1.35 !important;
            margin: 0.85em 0 0.35em !important;
          }
          .blog-rich-editor-canvas h4, .blog-rich-editor-canvas h5, .blog-rich-editor-canvas h6 {
            font-size: 1.1rem !important;
            font-weight: 600 !important;
            line-height: 1.4 !important;
            margin: 0.75em 0 0.3em !important;
          }
          .blog-rich-editor-canvas p {
            margin: 0.85em 0 !important;
            line-height: 1.75 !important;
          }
          .blog-rich-editor-canvas strong, .blog-rich-editor-canvas b {
            font-weight: 700 !important;
          }
          .blog-rich-editor-canvas em, .blog-rich-editor-canvas i {
            font-style: italic !important;
          }
          .blog-rich-editor-canvas u {
            text-decoration: underline !important;
          }
          .blog-rich-editor-canvas ul {
            list-style-type: disc !important;
            padding-left: 2em !important;
            margin: 0.75em 0 !important;
          }
          .blog-rich-editor-canvas ol {
            list-style-type: decimal !important;
            padding-left: 2em !important;
            margin: 0.75em 0 !important;
          }
          .blog-rich-editor-canvas li {
            display: list-item !important;
            margin: 0.35em 0 !important;
            line-height: 1.6 !important;
          }
          .blog-rich-editor-canvas blockquote {
            border-left: 4px solid #3b82f6 !important;
            padding-left: 1em !important;
            margin: 1em 0 !important;
            font-style: italic !important;
            color: ${darkMode ? '#d1d5db' : '#4b5563'} !important;
          }
          .blog-rich-editor-canvas a {
            color: #2563eb !important;
            text-decoration: underline !important;
            font-weight: 500 !important;
            cursor: pointer !important;
          }
          .blog-rich-editor-canvas a:hover {
            color: #1d4ed8 !important;
          }
        `}</style>
      </div>

      {/* ── Footer Stats ── */}
      <div
        className={`shrink-0 px-4 py-2.5 border-t flex items-center justify-between text-xs ${
          darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
        }`}
      >
        <div className="flex items-center gap-4 font-medium">
          <span>{stats.words.toLocaleString()} words</span>
          <span>{stats.chars.toLocaleString()} characters</span>
          <span>~{stats.readTime} min read</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
          <i className="ri-sparkling-line text-blue-500" />
          <span>Visual WYSIWYG Editor</span>
        </div>
      </div>

      {/* ── Insert Link Modal ── */}
      {showLinkModal && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div
            className={`w-full max-w-md p-5 rounded-2xl shadow-2xl border ${
              darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <i className="ri-link text-base" />
                </div>
                <h4 className="font-bold text-base">Insert Link</h4>
              </div>
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="space-y-3">
              {linkText && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Display Text:</label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    className={`w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Destination URL:</label>
                <input
                  type="url"
                  autoFocus
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyLink();
                    }
                  }}
                  className={`w-full px-3 py-2 text-sm rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'
                  }`}
                />
                <p className="text-[11px] text-gray-400 mt-1">Links open in a new tab. Protocol is added automatically.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyLink}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-check-line" />
                <span>Apply Link</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
